import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { motion, AnimatePresence, useMotionValue, useTransform, useAnimation } from 'framer-motion';
import type { PanInfo } from 'framer-motion';
import { ArrowUp } from 'lucide-react';
import { cn } from '@/utils/cn';
import { generateUUID } from '@/utils/uuid';
import { bookServiceApi } from '@/services/api';
import { UserAvatar } from './ChatAvatars';
import GemIcon from '@/components/icons/GemIcon';
import { analyticsService } from '@/services/analyticsService';
import { useQueryClient } from '@tanstack/react-query';
import { appendChatHistoryCache, useChatHistoryQuery } from '@/features/reading/hooks/useChatHistory';
import { useAuth } from '@/features/authentication/contexts/AuthContext';

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: number;
}

interface ReadingChatProps {
  open: boolean;
  onClose: () => void;
  contextText?: string;
  sectionId?: number;
  chapterId?: number;
  customAskFunction?: (chapterId: number, question: string) => Promise<string | { 
    answer: string;
    detail?: string;
    is_premium?: boolean; 
    remaining_requests?: number;
    gems_info?: { previous_gems: number; new_gems: number; gems_used: boolean; };
  }>;
  userAvatar?: string;
  bgColor?: 'white' | 'sepia' | 'dark';
  maxHeightVh?: number;
  bottomOffset?: string;
  onThinkingChange?: (isThinking: boolean) => void;
  onAssistantResponse?: () => void;
  onAssistantError?: (kind: 'insufficient_gems' | 'other') => void;
  onboardingPreview?: boolean;
  analyticsBookId?: number;
  analyticsBookTitle?: string;
  analyticsSectionId?: number;
  analyticsFlow?: 'onboarding' | 'normal';
  analyticsPageContext?: string;
}

// Default suggestion chips
const defaultSuggestions = [
  'این قسمت رو بیشتر توضیح بده',
  'کاربرد این محتوا تو زندگی واقعی چیه؟',
  'یه مثال واقعی بزن تا بهتر متوجه بشم',
];

const ReadingChat = ({ 
  open, 
  onClose, 
  sectionId, 
  chapterId,
  contextText = '',
  customAskFunction,
  userAvatar,
  bgColor = 'white',
  maxHeightVh = 70,
  bottomOffset,
  onThinkingChange,
  onAssistantResponse,
  onAssistantError,
  onboardingPreview = false,
  analyticsBookId,
  analyticsBookTitle,
  analyticsSectionId,
  analyticsFlow = 'normal',
  analyticsPageContext = 'read',
}: ReadingChatProps) => {
  // Background color classes based on theme
  const bgColorClasses = {
    white: 'bg-white',
    sepia: 'bg-[#f4ecd8]',
    dark: 'bg-gray-900'
  };
  
  const textColorClasses = {
    white: 'text-gray-800',
    sepia: 'text-[#5c4a2f]',
    dark: 'text-gray-100'
  };
  
  const dragHandleClasses = {
    white: 'bg-gray-300',
    sepia: 'bg-[#c4b89a]',
    dark: 'bg-gray-600'
  };
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const historyContext = sectionId ? 'section' : 'chapter';
  const historyContextId = sectionId ?? chapterId;
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const historyUserId = user?.id ?? 'anonymous';
  const historyKey = `${historyUserId}:${historyContext}:${historyContextId ?? ''}`;
  const historyQuery = useChatHistoryQuery(historyContext, historyContextId, open, historyUserId);
  const hydratedHistoryKeyRef = useRef<string | null>(null);
  const [input, setInput] = useState('');
  const listRef = useRef<HTMLDivElement | null>(null);
  const [isThinking, setIsThinking] = useState(false);
  const thinkingMsgIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!open || !historyContextId || hydratedHistoryKeyRef.current === historyKey) return;
    if (historyQuery.isLoading) return;
    hydratedHistoryKeyRef.current = historyKey;
    const turns = historyQuery.data?.items ?? [];
    setMessages(turns.flatMap(turn => {
      const timestamp = turn.created_at ? new Date(turn.created_at).getTime() : Date.now();
      return [
        { id: `${turn.chat_id}-user`, role: 'user' as const, content: turn.user_message, timestamp },
        { id: `${turn.chat_id}-assistant`, role: 'assistant' as const, content: turn.response, timestamp: timestamp + 1 },
      ];
    }));
  }, [open, historyContextId, historyKey, historyQuery.data, historyQuery.isLoading]);
  
  // Premium and gems state
  const [remainingRequests, setRemainingRequests] = useState<number | null>(null);
  const detectInsufficientGems = (message?: string) => {
    if (!message) return false;
    const normalized = message.toLowerCase();
    return normalized.includes('الماس') || normalized.includes('gem');
  };

  useEffect(() => {
    onThinkingChange?.(isThinking);
  }, [isThinking, onThinkingChange]);
  
  // Convert to Persian digits
  const toPersianDigits = useCallback((num: number): string => {
    return String(num).replace(/[0-9]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[parseInt(d, 10)]);
  }, []);

  // Drag to dismiss - Instagram-style smooth gestures
  const y = useMotionValue(0);
  const controls = useAnimation();
  
  // Sheet scales down slightly as you drag
  const sheetScale = useTransform(y, [0, 400], [1, 0.92]);
  // Border radius increases as you drag down for a nice effect
  const borderRadius = useTransform(y, [0, 100], [24, 32]);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' });
    }
  }, [messages, open]);

  // Animate in when opened - reset position and animate up
  useEffect(() => {
    if (open) {
      // Reset the motion value to start position
      y.set(window.innerHeight);
      // Then animate up with spring
      controls.start({ 
        y: 0,
        transition: { 
          type: 'spring', 
          damping: 30, 
          stiffness: 400 
        }
      });
    }
  }, [open, controls, y]);

  const handleDragEnd = useCallback((_: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    const shouldClose = info.offset.y > 100 || info.velocity.y > 300;
    
    if (shouldClose) {
      // Call onClose immediately so header reappears right away
      onClose();
      // Then animate out smoothly (AnimatePresence will handle exit animation)
    } else {
      // Snap back with satisfying spring
      controls.start({ 
        y: 0,
        transition: { 
          type: 'spring', 
          damping: 25, 
          stiffness: 500
        }
      });
    }
  }, [onClose, controls]);

  const handleSend = async (messageText?: string) => {
    const trimmed = (messageText || input).trim();
    if (!trimmed) return;

    const analyticsParams = {
      chat_type: 'nonfiction',
      message_source: messageText ? 'suggestion' : 'typed',
      book_id: analyticsBookId ? String(analyticsBookId) : 'unknown',
      book_title: analyticsBookTitle || 'unknown',
      ...(analyticsSectionId ? { section_id: String(analyticsSectionId) } : {}),
      page_context: analyticsPageContext,
      flow: analyticsFlow,
    };
    void analyticsService.logEvent('chat_message_sent', analyticsParams);

    const userMsg: ChatMessage = {
      id: generateUUID(),
      role: 'user',
      content: trimmed,
      timestamp: Date.now(),
    };
    setMessages((prev) => [...prev, userMsg]);
    setInput('');

    // Add thinking message with typing dots animation
    setIsThinking(true);
    onThinkingChange?.(true);
    const thinkingId = generateUUID();
    thinkingMsgIdRef.current = thinkingId;
    
    setMessages((prev) => [
      ...prev,
      {
        id: thinkingId,
        role: 'assistant',
        content: '', // Empty content, will show typing dots
        timestamp: Date.now() + 1,
      },
    ]);

    try {
      let answer: string;
      
      if (customAskFunction && chapterId) {
        const result = await customAskFunction(chapterId, trimmed);
        if (typeof result === 'string') {
          answer = result;
        } else {
          // Update remaining requests info
          if (result.remaining_requests !== undefined) {
            setRemainingRequests(result.remaining_requests);
          }
          
          // Check for detail message (insufficient gems or other errors)
          if ('detail' in result && result.detail) {
            throw new Error(result.detail);
          }
          
          answer = result.answer;
        }
      } else if (sectionId) {
        const res = await bookServiceApi.post<{ 
          answer?: string; 
          detail?: string;
          is_premium?: boolean;
          remaining_requests?: number;
          gems_info?: {
            previous_gems: number;
            new_gems: number;
            gems_used: boolean;
          };
        }>(
          `/sections/${sectionId}/ask`, 
          { question: trimmed }
        );
        
        // Update remaining requests info
        if (res.data?.remaining_requests !== undefined) {
          setRemainingRequests(res.data.remaining_requests);
        }
        
        // Check for detail message (insufficient gems or other errors)
        if (res.data?.detail) {
          // Show detail message as error
          throw new Error(res.data.detail);
        }
        
        if (!res.success || !res.data) {
          throw new Error(res.error || 'Failed to get response');
        }
        answer = res.data.answer || 'متاسفانه پاسخی دریافت نشد.';
      } else {
        throw new Error('Neither sectionId nor chapterId with customAskFunction provided');
      }
      
      // Convert English numbers to Persian in the answer
      const persianAnswer = answer.replace(/[0-9]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[parseInt(d, 10)]);
      
      setMessages((prev) =>
        prev.map((m) =>
          m.id === thinkingId
            ? { ...m, content: persianAnswer, timestamp: Date.now() }
            : m
        )
      );
      if (historyContextId) {
        appendChatHistoryCache(queryClient, historyUserId, historyContext, historyContextId, trimmed, persianAnswer);
      }
      
      // Callback: دریافت پاسخ موفق
      onAssistantResponse?.();
      void analyticsService.logEvent('chat_response_received', analyticsParams);
    } catch (error) {
      console.error('Chat error:', error);
      const errorMessage = error instanceof Error ? error.message : 'متاسفانه خطایی رخ داد. لطفاً دوباره تلاش کنید.';
      
      // Callback: خطا در چت
      onAssistantError?.(detectInsufficientGems(errorMessage) ? 'insufficient_gems' : 'other');
      
      // Convert English numbers to Persian in error message
      const persianErrorMessage = errorMessage.replace(/[0-9]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[parseInt(d, 10)]);
      setMessages((prev) =>
        prev.map((m) =>
          m.id === thinkingId
            ? {
                ...m,
                content: persianErrorMessage,
                timestamp: Date.now(),
              }
            : m
        )
      );
    } finally {
      setIsThinking(false);
      onThinkingChange?.(false);
      thinkingMsgIdRef.current = null;
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    handleSend(suggestion);
  };

  const messageVariants = useMemo(
    () => ({
      hidden: { opacity: 0, y: 10, scale: 0.95 },
      visible: { 
        opacity: 1, 
        y: 0, 
        scale: 1,
        transition: {
          duration: 0.2,
          ease: 'easeOut' as const
        }
      },
      exit: { 
        opacity: 0, 
        y: 10, 
        scale: 0.95,
        transition: {
          duration: 0.15
        }
      },
    }),
    []
  );

  const isEmptyState = messages.length === 0;

  return (
    <AnimatePresence initial={false}>
      {open && (
        <motion.div
          key="reading-chat-fullscreen"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[70]"
          style={{
            WebkitTapHighlightColor: 'transparent',
          }}
        >
          {/* Transparent backdrop - allows reading page background to show through */}
          <motion.div 
            className="absolute inset-0"
            onClick={onClose}
          />
          
          {/* Draggable Chat Container */}
          <motion.div
            drag="y"
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0.05, bottom: 0.6 }}
            onDragEnd={handleDragEnd}
            animate={controls}
            initial={false}
            style={{ 
              y, 
              scale: sheetScale,
              borderTopLeftRadius: borderRadius,
              borderTopRightRadius: borderRadius,
              height: onboardingPreview
                ? `min(${maxHeightVh}vh, calc(100dvh - var(--onboarding-header-h, 110px) - 0.5rem))`
                : `${maxHeightVh}vh`,
              maxHeight: onboardingPreview
                ? `min(${maxHeightVh}vh, calc(100dvh - var(--onboarding-header-h, 110px) - 0.5rem))`
                : `${maxHeightVh}vh`,
              paddingBottom: bottomOffset || undefined,
            }}
            className={cn(
              'absolute inset-x-0 bottom-0',
              bgColorClasses[bgColor],
              'flex flex-col',
              'overflow-hidden',
              'shadow-2xl'
            )}
            dir="rtl"
          >
            {/* Drag Handle Indicator */}
            <div className="flex justify-center pt-3 pb-2">
              <div className={cn('w-10 h-1 rounded-full', dragHandleClasses[bgColor])} />
            </div>
            
            {/* Messages Area */}
            <div 
              ref={listRef}
              className="flex-1 overflow-y-auto px-4 pt-2"
              style={{
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
              }}
            >
              <style>{`
                .chat-messages::-webkit-scrollbar {
                  display: none;
                }
              `}</style>
              {historyQuery.isLoading ? (
                <div className="flex h-full flex-col items-center justify-center gap-3" aria-live="polite">
                  <div className="h-8 w-8 rounded-full border-2 border-primary-500/30 border-t-primary-500 animate-spin" />
                  <p className={cn('font-persian text-sm', textColorClasses[bgColor])}>در حال بارگذاری تاریخچه گفتگو…</p>
                </div>
              ) : historyQuery.isError && messages.length === 0 ? (
                <div className="flex h-full flex-col items-center justify-center gap-3">
                  <p className={cn('font-persian text-sm', textColorClasses[bgColor])}>بارگذاری تاریخچه گفتگو انجام نشد.</p>
                  <button type="button" onClick={() => void historyQuery.refetch()} className="font-persian text-sm text-primary-600 underline">تلاش دوباره</button>
                </div>
              ) : isEmptyState ? (
                /* Empty state with mascot animation and instruction text */
                <div className="flex flex-col items-center justify-center h-full gap-4">
                  <img
                    src="/character.webp"
                    alt=""
                    className={cn(
                      'object-contain',
                      onboardingPreview ? 'h-[155px] w-[155px]' : 'h-[164px] w-[164px]'
                    )}
                    aria-hidden="true"
                  />
                  <p className={cn(
                    'font-persian text-center',
                    onboardingPreview ? 'text-sm px-5' : 'text-base px-6',
                    textColorClasses[bgColor]
                  )}>
                    هر سوالی درباره این قسمت داری بپرس
                  </p>
                </div>
              ) : (
                /* Messages List - increased spacing for better whitespace */
                <div className={cn(onboardingPreview ? 'space-y-5' : 'space-y-6', 'chat-messages')}>
                  {messages.map((m, index) => (
                    <motion.div
                      key={m.id}
                      variants={messageVariants}
                      initial="hidden"
                      animate="visible"
                      exit="exit"
                      transition={{ delay: index * 0.03 }}
                      className={cn(
                        'flex items-end gap-2',
                        m.role === 'user' ? 'flex-row' : 'flex-row-reverse'
                      )}
                    >
                      {/* Avatar */}
                      {m.role === 'user' ? (
                        <UserAvatar size="sm" />
                      ) : (
                        <img
                          src="/character.webp"
                          alt=""
                          className={cn(
                            'shrink-0 object-contain',
                            onboardingPreview ? 'h-8 w-8' : 'h-8 w-8'
                          )}
                          aria-hidden="true"
                        />
                      )}
                      
                      {/* Message Bubble */}
                      <div
                        className={cn(
                          onboardingPreview
                            ? 'px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed font-persian whitespace-pre-wrap'
                            : 'px-4 py-3 rounded-2xl text-sm leading-relaxed font-persian whitespace-pre-wrap',
                          'max-w-[80%]',
                          m.role === 'user'
                            ? cn(
                                'rounded-br-md',
                                bgColor === 'dark' 
                                  ? 'bg-gray-800 text-gray-100 border border-gray-700'
                                  : bgColor === 'sepia'
                                  ? 'bg-[#e5dcc3] text-[#5c4a2f] border border-[#d4c4a8]'
                                  : 'bg-soft-neutral-200 text-foreground'
                              )
                            : cn(
                                'rounded-bl-md shadow-sm',
                                bgColor === 'dark'
                                  ? 'bg-gradient-to-br from-purple-600 to-purple-700 text-white'
                                  : bgColor === 'sepia'
                                  ? 'bg-gradient-to-br from-[#8B5A2B] to-[#6B4423] text-[#f4ecd8]'
                                  : 'bg-gradient-to-br from-primary-500 to-primary-600 text-white'
                              )
                        )}
                      >
                        {m.id === thinkingMsgIdRef.current ? (
                          <div className="flex items-center gap-1 py-1 px-0.5">
                            <span className="typing-dot" />
                            <span className="typing-dot" style={{ animationDelay: '0.16s' }} />
                            <span className="typing-dot" style={{ animationDelay: '0.32s' }} />
                          </div>
                        ) : (
                          m.content
                        )}
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>

            {/* Suggestion Chips - Hidden scrollbar */}
            {!isThinking && (
              <div 
                className={onboardingPreview ? 'px-3.5 py-2 overflow-x-auto' : 'px-4 py-2 overflow-x-auto'}
                style={{
                  scrollbarWidth: 'none',
                  msOverflowStyle: 'none',
                }}
              >
                <style>{`
                  .suggestion-chips::-webkit-scrollbar {
                    display: none;
                  }
                `}</style>
                <div className="flex gap-2 suggestion-chips" style={{ direction: 'rtl' }}>
                  {defaultSuggestions.map((suggestion, index) => (
                    <button
                      key={index}
                      onClick={() => handleSuggestionClick(suggestion)}
                      className={cn(
                        onboardingPreview
                          ? 'px-3.5 py-2 rounded-full text-sm font-persian whitespace-nowrap'
                          : 'px-4 py-2 rounded-full text-sm font-persian whitespace-nowrap',
                        'active:scale-95 transition-all duration-200',
                        bgColor === 'dark' 
                          ? 'bg-gray-800 text-gray-200 border border-gray-700 hover:bg-gray-700'
                          : bgColor === 'sepia'
                          ? 'bg-[#e5dcc3] text-[#5c4a2f] border border-[#d4c4a8] hover:bg-[#d9cdb4]'
                          : 'bg-soft-neutral-100 text-foreground border border-soft-neutral-200 hover:bg-soft-neutral-200'
                      )}
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Remaining requests indicator */}
            {remainingRequests !== null && (
              <div className={cn(
                onboardingPreview ? 'px-3 py-1 text-[11px] text-center font-persian' : 'px-4 py-2 text-center text-xs font-persian',
                bgColor === 'dark' ? 'text-gray-400' : bgColor === 'sepia' ? 'text-[#8a7a5a]' : 'text-gray-500'
              )}>
                <span>
                  تعداد درخواست‌های باقی‌مانده امروز: {toPersianDigits(remainingRequests)}
                </span>
              </div>
            )}

            {/* Input Area */}
            <form
              className={cn(
                onboardingPreview ? 'px-3 py-2 border-t flex flex-row-reverse gap-1.5' : 'px-4 py-3 border-t flex flex-row-reverse gap-2',
                bgColor === 'dark' 
                  ? 'border-gray-700 bg-gray-900'
                  : bgColor === 'sepia'
                  ? 'border-[#d4c4a8] bg-[#f4ecd8]'
                  : 'border-soft-neutral-200 bg-gray-50'
              )}
              style={{
                paddingBottom: 'env(safe-area-inset-bottom)',
              }}
              onSubmit={(e) => {
                e.preventDefault();
                handleSend();
              }}
            >
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="سوالاتو بپرس..."
                disabled={isThinking}
                className={cn(
                  onboardingPreview ? 'flex-1 h-12 px-3.5 rounded-2xl text-sm font-persian' : 'flex-1 h-12 px-4 rounded-2xl text-sm font-persian',
                  'focus:outline-none focus:ring-2 focus:ring-primary',
                  'disabled:opacity-50 disabled:cursor-not-allowed',
                  'transition-all duration-200',
                  bgColor === 'dark'
                    ? 'bg-gray-800 border border-gray-700 text-white placeholder:text-gray-500 focus:bg-gray-750'
                    : bgColor === 'sepia'
                    ? 'bg-[#e5dcc3] border border-[#d4c4a8] text-[#2d2416] placeholder:text-[#8a7a5a] focus:bg-white'
                    : 'bg-gray-100 border border-transparent text-gray-900 placeholder:text-gray-400 focus:bg-white'
                )}
                style={{ lineHeight: '48px' }}
                dir="rtl"
              />
              <button
                type="submit"
                disabled={!input.trim() || isThinking}
                className={cn(
                  onboardingPreview ? "flex items-center justify-center w-12 h-12 rounded-full shadow-sm flex-shrink-0 text-white" : "flex items-center justify-center w-12 h-12 rounded-full shadow-sm flex-shrink-0 text-white",
                  "disabled:bg-slate-500 disabled:text-white disabled:cursor-not-allowed transition-transform active:scale-95",
                  !input.trim() || isThinking
                    ? 'bg-gray-300 text-gray-500'
                    : bgColor === 'dark'
                    ? 'bg-purple-600 hover:bg-purple-700'
                    : bgColor === 'sepia'
                    ? 'bg-[#8B5A2B] hover:bg-[#6B4423]'
                    : 'bg-primary-500 hover:bg-primary-600'
                )}
                aria-label="ارسال پیام"
              >
                {remainingRequests !== null && remainingRequests === 0 ? (
                  // Show 20 with gem icon when user has no remaining requests
                  <div className="flex items-center justify-center gap-0.5" dir="rtl">
                    <span className="text-sm font-bold text-white">۲۰</span>
                    <GemIcon size={12} color="flatWhite" className="flex-shrink-0 -translate-y-px" />
                  </div>
                ) : (
                  <ArrowUp className="w-5 h-5" />
                )}
              </button>
            </form>

            <style>{`
              .typing-dot {
                width: 7px;
                height: 7px;
                border-radius: 9999px;
                background: rgba(255,255,255,0.92);
                animation: typingBounce 0.9s infinite ease-in-out;
                display: inline-block;
              }
              @keyframes typingBounce {
                0%, 70%, 100% { transform: translateY(0); opacity: 0.35; }
                35% { transform: translateY(-5px); opacity: 1; }
              }
            `}</style>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ReadingChat;
